import { Component, Prop, h, Host, State, Build } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * App download banner component for SignNow brand.
 * Shows only on mobile devices with platform-specific content.
 * Supports server-side rendering when the `userAgent` prop is provided.
 *
 * @product signnow
 * @version 1.2.0
 * @category SignNow
 * @contentConfig Mobile app download banner with platform detection
 * @example
 *   <sn-app-banner
 *     values='{"title":"Download signNow app","rating":"4.7","ratingText":"/ 5 rating on","appStoreText":"App Store","googlePlayText":"Google Play","appStoreUrl":"https://itunes.apple.com/app/apple-store/id489262811?pt=482524&ct=main_site_footer&mt=8","googlePlayUrl":"https://play.google.com/store/apps/details?id=com.signnow.android&referrer=utm_source%3Dmain_site_footer"}'
 *   ></sn-app-banner>
 */
@Component({
  tag: 'sn-app-banner',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNAppBanner {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * @dynamic
   * @type {object}
   * @property {string} title - Banner title text
   * @property {string} rating - App rating value (e.g. "4.7")
   * @property {string} ratingText - Rating description text (e.g. "/ 5 rating on")
   * @property {string} appStoreText - App Store label text
   * @property {string} googlePlayText - Google Play label text
   * @property {string} appStoreUrl - App Store download link
   * @property {string} googlePlayUrl - Google Play download link
   * @required title
   * @required appStoreUrl
   * @required googlePlayUrl
   */
  @Prop({ mutable: true }) values?: {
    title?: string;
    rating?: string;
    ratingText?: string;
    appStoreText?: string;
    googlePlayText?: string;
    appStoreUrl?: string;
    googlePlayUrl?: string;
  } | string;

  /**
   * User-Agent string for SSR platform detection.
   */
  @Prop() userAgent?: string;

  @State() isIOS: boolean = false;
  @State() isAndroid: boolean = false;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.detectPlatform();
  }

  private detectPlatform() {
    const ua = this.userAgent ?? (Build.isBrowser ? window.navigator.userAgent : '');
    if (ua) {
      const uaLower = ua.toLowerCase();
      this.isIOS = /iphone|ipad|ipod/.test(uaLower);
      this.isAndroid = /android/.test(uaLower);
    }
  }

  render() {
    const v = typeof this.values === 'object' && this.values !== null
      ? this.values as Record<string, string>
      : {};

    const title = v.title ?? 'Download signNow app';
    const rating = v.rating ?? '4.7';
    const ratingText = v.ratingText ?? '/ 5 rating on';
    const appStoreText = v.appStoreText ?? 'App Store';
    const googlePlayText = v.googlePlayText ?? 'Google Play';
    const appStoreUrl = v.appStoreUrl ?? 'https://itunes.apple.com/app/apple-store/id489262811?pt=482524&ct=main_site_footer&mt=8';
    const googlePlayUrl = v.googlePlayUrl ?? 'https://play.google.com/store/apps/details?id=com.signnow.android&referrer=utm_source%3Dmain_site_footer';

    const downloadLink = this.isIOS ? appStoreUrl : googlePlayUrl;
    const storeText = this.isIOS ? appStoreText : googlePlayText;

    // The store is only known in the browser, so the server renders the row empty (its height is
    // reserved in CSS) and the client fills it in. Both states are ONE element with `aria-hidden`
    // spelled out in each: rendering them as two separate branches left the SSR node in place on
    // hydration, keeping its `aria-hidden="true"` while the real, focusable download link was
    // patched in underneath — an aria-hidden subtree with a focusable child. Attributes present in
    // the vnode are always applied; ones merely omitted are not removed.
    const isReserve = !this.isIOS && !this.isAndroid;

    return (
      <Host data-color-mode={this.colorMode}>
        <div class="app-banner">
          <div class="app-banner__row" aria-hidden={isReserve ? 'true' : 'false'}>
            {!isReserve && [
              <div class="app-banner__column">
                <div class="app-banner__title">{title}</div>
                <div class="app-banner__description">
                  <span class="rating-accent">{rating}</span> {ratingText}{' '}
                  <span class={`description-text ${this.isIOS ? 'description-text__app-store' : 'description-text__google-play'}`}>{storeText}</span>
                </div>
              </div>,
              <div class="app-banner__column">
                <div class={`download-btn ${this.isIOS ? 'download-btn__app-store' : 'download-btn__google-play'}`}>
                  <a href={downloadLink} title="Download signNow app" target="_blank" rel="noopener">
                    <span class="download-btn-logo"></span>
                  </a>
                </div>
              </div>,
            ]}
          </div>
        </div>
      </Host>
    );
  }
}
